/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { isValidInn, PARTY_KIND_LABEL } from '@kd/shared';
import { api } from '@/lib/api';
import { Button, ErrorText, Field, Input, Select, Textarea } from './ui';

const split = (s: string) => s.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean);

export function PartyForm({ party, onSaved }: { party?: any; onSaved: (p: any) => void }) {
  const [f, setF] = useState({
    kind: party?.kind ?? 'LEGAL', name: party?.name ?? '', shortName: party?.shortName ?? '', inn: party?.inn ?? '', ogrn: party?.ogrn ?? '',
    kpp: party?.kpp ?? '', address: party?.address ?? '', postalAddress: party?.postalAddress ?? '', birthDate: party?.birthDate ?? '',
    phones: (party?.phones ?? []).join(', '), emails: (party?.emails ?? []).join(', '), website: party?.website ?? '',
    isClient: party?.isClient ?? true, isCounterparty: party?.isCounterparty ?? false, tags: (party?.tags ?? []).join(', '),
    source: party?.source ?? '', notes: party?.notes ?? '',
  });
  const set = (k: string, v: unknown) => setF((s) => ({ ...s, [k]: v }));
  const lookup = useMutation({
    mutationFn: () => api.get(`/parties/lookup/${f.inn.trim()}`),
    onSuccess: (d) => setF((s) => ({ ...s, kind: d.kind, name: d.name ?? s.name, shortName: d.shortName ?? s.shortName, ogrn: d.ogrn ?? '', kpp: d.kpp ?? '', address: d.address ?? s.address })),
  });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...f, phones: split(f.phones), emails: split(f.emails), tags: split(f.tags),
        inn: f.inn || null, ogrn: f.ogrn || null, kpp: f.kpp || null, birthDate: f.birthDate || null, shortName: f.shortName || null,
      };
      return party ? api.patch(`/parties/${party.id}`, body) : api.post('/parties', body);
    },
    onSuccess: onSaved,
  });
  const innBad = f.inn && !isValidInn(f.inn.trim());
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Вид"><Select value={f.kind} onChange={(e) => set('kind', e.target.value)} options={Object.entries(PARTY_KIND_LABEL).map(([value, label]) => ({ value, label }))} /></Field>
        <Field label="ИНН" className="sm:col-span-2" hint={innBad ? 'ИНН не проходит проверку контрольной суммы' : undefined}>
          <div className="flex gap-2">
            <Input value={f.inn} onChange={(e) => set('inn', e.target.value)} />
            {f.kind !== 'PERSON' && <Button type="button" variant="secondary" disabled={!f.inn || !!innBad || lookup.isPending} onClick={() => lookup.mutate()}><Search size={14} /> Заполнить по ИНН</Button>}
          </div>
        </Field>
      </div>
      <ErrorText error={lookup.error} />
      <Field label={f.kind === 'PERSON' ? 'ФИО' : 'Полное наименование'}><Input required value={f.name} onChange={(e) => set('name', e.target.value)} /></Field>
      {f.kind !== 'PERSON' ? (
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Краткое наименование"><Input value={f.shortName} onChange={(e) => set('shortName', e.target.value)} /></Field>
          <Field label={f.kind === 'IP' ? 'ОГРНИП' : 'ОГРН'}><Input value={f.ogrn} onChange={(e) => set('ogrn', e.target.value)} /></Field>
          {f.kind === 'LEGAL' && <Field label="КПП"><Input value={f.kpp} onChange={(e) => set('kpp', e.target.value)} /></Field>}
        </div>
      ) : (
        <Field label="Дата рождения"><Input type="date" value={f.birthDate} onChange={(e) => set('birthDate', e.target.value)} /></Field>
      )}
      <Field label="Адрес"><Input value={f.address} onChange={(e) => set('address', e.target.value)} /></Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Телефоны" hint="Через запятую"><Input value={f.phones} onChange={(e) => set('phones', e.target.value)} /></Field>
        <Field label="Почта" hint="Через запятую"><Input value={f.emails} onChange={(e) => set('emails', e.target.value)} /></Field>
        <Field label="Теги" hint="Через запятую"><Input value={f.tags} onChange={(e) => set('tags', e.target.value)} /></Field>
        <Field label="Источник"><Input value={f.source} onChange={(e) => set('source', e.target.value)} /></Field>
      </div>
      <div className="flex gap-5 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.isClient} onChange={(e) => set('isClient', e.target.checked)} /> Клиент</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.isCounterparty} onChange={(e) => set('isCounterparty', e.target.checked)} /> Контрагент</label>
      </div>
      <Field label="Заметки"><Textarea value={f.notes} onChange={(e) => set('notes', e.target.value)} /></Field>
      <ErrorText error={save.error} />
      <div className="flex justify-end"><Button type="submit" disabled={save.isPending}>{save.isPending ? 'Сохранение...' : 'Сохранить'}</Button></div>
    </form>
  );
}
